/**
 * The Lal Kitab house check's form props, frames, slot floors and ids, shared by
 * the server-rendered shell (LalKitabHouseCheckLazy), the island's
 * BirthDetailsForm and BirthToolFormShell.parity.test.tsx, so every frame before
 * and after the island loads reads the same words. One check serves the 18
 * /lal-kitab-remedies/<planet>/ pages (English and Hindi) and the /lal-kitab/
 * hub's chart summary.
 *
 * No name field: the answer does not use one. The birth time is required: Lal
 * Kitab reads every graha by its house from the Lagna, which moves into the
 * next sign about every two hours. The place must be picked from the list,
 * since POST /lal-kitab-kundli takes the coordinates, not a typed name.
 *
 * The Hindi pages' controls (LAL_KITAB_HOUSE_CHECK_HI_FORM and
 * LAL_KITAB_HOUSE_CHECK_HI_COPY) are Devanagari (owner decision 2026-10-28,
 * which replaced the 2026-10-08 Hinglish rule), the register of
 * BirthDetailsForm.i18n.ts: tests/hindi-register.test.ts keeps Latin-letter
 * Hinglish out of the Hindi copy. The Hindi section's heading and lead and the
 * Hindi answer are page copy and are Devanagari in their components
 * (LalKitabHouseCheckSection.tsx, LalKitabHouseAnswerHi.tsx).
 */
export const LAL_KITAB_HOUSE_CHECK_FORM = {
  language: "en",
  submitLabel: "Check My Lal Kitab Chart",
  collectName: false,
  requireBirthTime: true,
} as const;

/**
 * One box for every state of the check's slot (the career check's frame): the
 * shell, the loading shell, the live form and the answer each fill the slot's
 * reserved height, and the submit sits at the foot of the box in every state
 * with one 48 px height.
 */
export const LAL_KITAB_HOUSE_CHECK_FRAME =
  "mx-auto flex w-full max-w-md flex-1 flex-col [&>form]:flex [&>form]:flex-1 [&>form]:flex-col [&>form>:last-child]:mt-auto [&>form>:last-child]:min-h-12";

/** The answer card takes the section's width (the remedy pages' and hub's column). */
export const LAL_KITAB_HOUSE_ANSWER_FRAME = "flex w-full flex-1 flex-col";

/**
 * The form's room: the slot's floor and, once the answer's room is added, the
 * form frame's own height, so the submit stays where the press found it.
 * Measured 2026-10-22 on next dev (Playwright, service workers blocked, on
 * /lal-kitab-remedies/surya/, /hi/lal-kitab-remedies/chandra/ and /lal-kitab/,
 * 7 widths from 320 to 1280): the shell and the empty form are 434 at 320, 414
 * at 360-414, 398 at 480 and 356 from 640; with the field errors of an empty
 * submit 512 at 320, 476 at 360-414, 460 at 480 and 414 from 640; with the
 * "pick a city from the list" error 472 at 320 (452 on the Hindi card at
 * 360-414) and 378 from 640. No layout shift on load or on activation.
 */
export const LAL_KITAB_HOUSE_FORM_RESERVE = "min-h-[520px] sm:min-h-[460px]";

/**
 * The answer's room on a remedy page (one graha), added by the island inside
 * the submit itself, before the request goes out (the career check's pattern,
 * #2777): the growth happens in the press, an input-driven change rather than a
 * layout shift, and the reply, an error or a retry all land inside it; only
 * "Check another birth date" releases it. Measured 2026-10-22 with POST
 * /lal-kitab-kundli mocked (same runs as the form): a comfortable graha with no
 * rin is 510 at 320 and 334 from 640; the Sun needing upay with four rin
 * elsewhere 711 at 320 and 520 at 640; the Moon needing upay with two rin of its
 * own and two elsewhere 988 at 320, 892 at 360 and 727 at 640. The tallest, two
 * rin of its own and ten elsewhere across five grahas (twelve is the most a
 * chart can raise: at most two per graha, Rahu and Ketu never both), is 1115 at
 * 320, 1018 at 360, 961 at 390 and 791 at 640; the Hindi card's tallest is 849
 * at 320. Hence 1160 px on phones and 840 from the sm breakpoint.
 */
export const LAL_KITAB_GRAHA_ANSWER_RESERVE = "min-h-[1160px] sm:min-h-[840px]";

/**
 * The answer's room on the /lal-kitab/ hub: the counts, the nine rows and one
 * line per rin. Measured as above: four rin are 923 at 320, 809 at 360-414 and
 * 788 from 480; twelve rin 1147 at 320, 985 at 360-414 and 964 from 480. Hence
 * 1200 px on phones and 1020 from the sm breakpoint.
 */
export const LAL_KITAB_CHART_ANSWER_RESERVE = "min-h-[1200px] sm:min-h-[1020px]";

/** The real form's input ids (no heading prop, so BirthDetailsForm's "-default" ids). */
export const LAL_KITAB_HOUSE_CHECK_FIELD_IDS = {
  date: "birthDate-default",
  time: "birthTime-default",
  place: "birthPlace-default",
} as const;

/**
 * The id of the remedy page's upay cards (LalKitabRemedyPage), which the answer
 * links for every upay beyond the engine's one ritual.
 */
export const LAL_KITAB_UPAY_ANCHOR = "lal-kitab-upay";

/** free_tool_started and calculator_used placements, one per surface. */
export const LAL_KITAB_HOUSE_CHECK_PLACEMENT = {
  remedyEn: "lal_kitab_remedy_page_check",
  remedyHi: "hi_lal_kitab_remedy_page_check",
  hub: "lal_kitab_hub_check",
} as const;

/**
 * The Hindi check's controls: the submit, the failure lines and the answer's
 * button. "लाल किताब कुंडली देखें" stays on one line at 320 px (a label that
 * wraps grows back when the one-line loading label replaces it).
 */
export const LAL_KITAB_HOUSE_CHECK_HI_COPY = {
  submit: "लाल किताब कुंडली देखें",
  unavailable: "गणना नहीं हो पाई — कृपया दोबारा कोशिश करें।",
  unreadable: "यह परिणाम पढ़ा नहीं जा सका। कृपया दोबारा कोशिश करें।",
  checkAnother: "दूसरी जन्म तिथि जाँचें",
  pickPlace: "सुझावों में से अपना जन्म स्थान चुनें।",
} as const;

/** The Hindi form props, shared by the shell, the island and the parity test. */
export const LAL_KITAB_HOUSE_CHECK_HI_FORM = {
  language: "hi",
  submitLabel: LAL_KITAB_HOUSE_CHECK_HI_COPY.submit,
  collectName: false,
  requireBirthTime: true,
} as const;
